import { getConfig } from '../../config.js';
import { i18n } from '../globalization/i18n.js';
import { default as moment } from "moment";
import { default as numeral } from "numeral";

const WIRE_UTC_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/;
const WIRE_ZONED_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?[+-]\d{2}:\d{2}$/;
const WIRE_LOCAL_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?$/;
const WIRE_LOCAL_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const WIRE_LOCAL_TIME_RE = /^\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?$/;

const WIRE_UTC_DATETIME_FORMATS = [
    'YYYY-MM-DDTHH:mm:ss[Z]',
    'YYYY-MM-DDTHH:mm:ss.S[Z]',
    'YYYY-MM-DDTHH:mm:ss.SS[Z]',
    'YYYY-MM-DDTHH:mm:ss.SSS[Z]',
];
const WIRE_ZONED_DATETIME_FORMATS = [
    'YYYY-MM-DDTHH:mm:ssZ',
    'YYYY-MM-DDTHH:mm:ss.SZ',
    'YYYY-MM-DDTHH:mm:ss.SSZ',
    'YYYY-MM-DDTHH:mm:ss.SSSZ',
];
const WIRE_LOCAL_DATETIME_FORMATS = [
    'YYYY-MM-DDTHH:mm:ss',
    'YYYY-MM-DDTHH:mm:ss.S',
    'YYYY-MM-DDTHH:mm:ss.SS',
    'YYYY-MM-DDTHH:mm:ss.SSS',
];
const WIRE_LOCAL_TIME_FORMATS = [
    'HH:mm:ss',
    'HH:mm:ss.S',
    'HH:mm:ss.SS',
    'HH:mm:ss.SSS',
];

// ----------------------------------------------------------------------------------

function formatNumber(value, format) {

    if (value === null || value === undefined)
        return null;
    return numeral(value).format(format);

};

function parseNumber(value, format) {

    if (value === null || value === undefined)
        return null;
    return numeral(value, format).value();

};

function formatBool(value, format) {

    if (value === null || value === undefined)
        return null;
    if (value === true)
        return i18n.t('data.value.true');
    if (value === false)
        return i18n.t('data.value.false');
    return i18n.t('data.value.undefined');

};

function parseBool(value, format) {

    if (value === null || value === undefined)
        return null;
    if (value == i18n.t('data.value.true'))
        return true;
    if (value == i18n.t('data.value.false'))
        return false;
    return null;

};

function formatTimespan(value, valueUnit = 'seconds', format = 'hours') {

    if (value === null || value === undefined || value === '')
        return null;

    let d = moment.duration(value, valueUnit);

    if (format == 'hours') {

        const hours = Math.floor(d.asHours());
        const minutes = d.minutes();
        return `${hours}:${minutes.toString().padStart(2, '0')}`;

    }

    const minutes = Math.floor(d.asMinutes());
    const seconds = d.seconds();
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;

};

function parseTimespan(value, valueUnit = 'seconds', format = 'hours') {

    if (value === null || value === undefined)
        return null;
    let d = format == 'minutes' ? moment.duration(value) : moment.duration(value + ':00');
    if (format == 'minutes')
        return d.asMinutes();
    return d.asSeconds();

};

function formatString(value, format) {

    if (value === null || value === undefined)
        return null;
    return value.toString();

};

function parseString(value, format) {

    return value;

};

function formatBytes(value, format = '0.00 b') {

    if (value === null || value === undefined)
        return null;
    return numeral(value).format(format);

};

function parseBytes(value, format = '0.00 b') {

    if (value === null || value === undefined)
        return null;
    return numeral(value, format).value();

};

// ----------------------------------------------------------------------------------

function newDatetime(locale) {

    const config = getConfig();

    locale = locale || config.locale;
    return moment(new Date()).locale(locale);

};

function newTzDatetime(timeZone, locale) {

    const config = getConfig();

    timeZone = timeZone || config.timeZone;
    locale = locale || config.locale;
    //return moment.tz(new Date(), locale, timeZone);
    return moment.utc().tz(timeZone).locale(locale);

};

function normalizeDatetimeParsingFormats(formats) {

    const config = getConfig();

    formats = formats || [config.datetimeFormat];
    if (!Array.isArray(formats))
        formats = [formats];
    if (formats.length == 0)
        formats = [config.datetimeFormat];
    if (formats.indexOf(moment.ISO_8601) < 0)
        formats.push(moment.ISO_8601);
    return formats;

};

function parseDatetime(value, formats, locale) {

    const config = getConfig();

    if (value === undefined)
        return null;
    if (value === null)
        return null;

    formats = normalizeDatetimeParsingFormats(formats);
    locale = locale || config.locale;

    if (moment.isMoment(value))
        return value;

    if (typeof value == 'string')
        return moment(value, formats, locale);

    return moment(value).locale(locale);

};

function parseTzDatetime(timeZone, value, formats, locale) {

    if (value === undefined)
        return null;
    if (value === null)
        return null;

    const config = getConfig();

    timeZone = timeZone || config.timeZone;
    formats = normalizeDatetimeParsingFormats(formats);
    locale = locale || config.locale;

    if (moment.isMoment(value))
        return value.clone().tz(timeZone).locale(locale);

    if (typeof value != 'string')
        return moment.tz(value, timeZone).locale(locale);

    if (/[zZ]|[+-]\d{2}:\d{2}$/.test(value))
        return moment.parseZone(value).tz(timeZone).locale(locale);

    return moment.tz(value, formats, locale, timeZone);

};

function formatDatetime(value, format, locale) {

    if (value === undefined)
        return null;
    if (value === null)
        return null;

    const config = getConfig();

    format = format || config.datetimeFormat;
    locale = locale || config.locale;

    if (typeof value == 'string')
        return parseDatetime(value, normalizeDatetimeParsingFormats(format), locale).format(format);

    return moment(value).locale(locale).format(format);

};

function formatTzDatetime(timeZone, value, format, locale) {

    if (value === undefined)
        return null;
    if (value === null)
        return null;

    const config = getConfig();

    timeZone = timeZone || config.timeZone;
    format = format || config.datetimeFormat;
    locale = locale || config.locale;

    if (typeof value == 'string')
        return parseTzDatetime(timeZone, value, normalizeDatetimeParsingFormats(format), locale).format(format);

    return moment.tz(value, timeZone).locale(locale).format(format);

};

function formatIsoDatetime(value, locale) {

    if (value === undefined)
        return null;
    if (value === null)
        return null;

    const config = getConfig();

    locale = locale || config.locale;

    if (typeof value == 'string')
        return moment(value, [config.datetimeFormat, moment.ISO_8601], locale).format(formatDatetime);

    return moment(value).locale(locale).toISOString();

};

// ----------------------------------------------------------------------------------

function isDatetimeLikeValue(value) {

    return value instanceof Date || moment.isMoment(value);

}

function getCanonicalFraction(momentValue) {

    const milliseconds = momentValue.millisecond();
    if (!milliseconds)
        return '';
    return `.${milliseconds.toString().padStart(3, '0').replace(/0+$/, '')}`;

}

function throwInvalidWireValue(type, value) {

    throw new Error(`Invalid ${type} wire value: ${value}`);

}

function ensureStringMatchesWirePattern(type, value, pattern) {

    if (typeof value != 'string' || !pattern.test(value))
        throwInvalidWireValue(type, value);
    return value;

}

function ensureValidMoment(type, value, parser) {

    const parsed = parser(value);

    if (!(parsed && parsed.isValid && parsed.isValid()))
        throwInvalidWireValue(type, value);

    return parsed;

}

function parseWireUtcDatetime(value) {

    if (value === undefined || value === null)
        return null;
    if (moment.isMoment(value))
        return value.clone().utc();
    if (value instanceof Date)
        return moment.utc(value);

    const actualValue = ensureStringMatchesWirePattern('utc_datetime', value, WIRE_UTC_DATETIME_RE);
    return ensureValidMoment('utc_datetime', value, (input) => moment.utc(input, WIRE_UTC_DATETIME_FORMATS, true));

}

function serializeWireUtcDatetime(value) {

    if (value === undefined || value === null)
        return null;

    const parsed = isDatetimeLikeValue(value)
        ? ensureValidMoment('utc_datetime', value, (input) => moment(input))
        : parseWireUtcDatetime(value);
    const utcValue = parsed.clone().utc();

    return `${utcValue.format('YYYY-MM-DD[T]HH:mm:ss')}${getCanonicalFraction(utcValue)}Z`;

}

function parseWireZonedDatetime(value) {

    if (value === undefined || value === null)
        return null;
    if (moment.isMoment(value))
        return value.clone();
    if (value instanceof Date)
        return moment(value);

    ensureStringMatchesWirePattern('zoned_datetime', value, WIRE_ZONED_DATETIME_RE);
    return ensureValidMoment('zoned_datetime', value, (input) => moment.parseZone(input, WIRE_ZONED_DATETIME_FORMATS, true));

}

function serializeWireZonedDatetime(value) {

    if (value === undefined || value === null)
        return null;

    const parsed = isDatetimeLikeValue(value)
        ? ensureValidMoment('zoned_datetime', value, (input) => moment.isMoment(input) ? input.clone() : moment(input))
        : parseWireZonedDatetime(value);

    return `${parsed.format('YYYY-MM-DD[T]HH:mm:ss')}${getCanonicalFraction(parsed)}${parsed.format('Z')}`;

}

function parseWireLocalDatetime(value) {

    if (value === undefined || value === null)
        return null;
    if (moment.isMoment(value))
        return value.clone();
    if (value instanceof Date)
        return moment(value);

    ensureStringMatchesWirePattern('local_datetime', value, WIRE_LOCAL_DATETIME_RE);
    return ensureValidMoment('local_datetime', value, (input) => moment(input, WIRE_LOCAL_DATETIME_FORMATS, true));

}

function serializeWireLocalDatetime(value) {

    if (value === undefined || value === null)
        return null;

    const parsed = isDatetimeLikeValue(value)
        ? ensureValidMoment('local_datetime', value, (input) => moment.isMoment(input) ? input.clone() : moment(input))
        : parseWireLocalDatetime(value);

    return `${parsed.format('YYYY-MM-DD[T]HH:mm:ss')}${getCanonicalFraction(parsed)}`;

}

function parseWireLocalDate(value) {

    if (value === undefined || value === null)
        return null;
    if (moment.isMoment(value))
        return value.clone();
    if (value instanceof Date)
        return moment(value);

    ensureStringMatchesWirePattern('local_date', value, WIRE_LOCAL_DATE_RE);
    return ensureValidMoment('local_date', value, (input) => moment(input, 'YYYY-MM-DD', true));

}

function serializeWireLocalDate(value) {

    if (value === undefined || value === null)
        return null;

    const parsed = isDatetimeLikeValue(value)
        ? ensureValidMoment('local_date', value, (input) => moment.isMoment(input) ? input.clone() : moment(input))
        : parseWireLocalDate(value);

    return parsed.format('YYYY-MM-DD');

}

function parseWireLocalTime(value) {

    if (value === undefined || value === null)
        return null;
    if (moment.isMoment(value))
        return value.clone();
    if (value instanceof Date)
        return moment(value);

    ensureStringMatchesWirePattern('local_time', value, WIRE_LOCAL_TIME_RE);
    return ensureValidMoment('local_time', value, (input) => moment(input, WIRE_LOCAL_TIME_FORMATS, true));

}

function serializeWireLocalTime(value) {

    if (value === undefined || value === null)
        return null;

    const parsed = isDatetimeLikeValue(value)
        ? ensureValidMoment('local_time', value, (input) => moment.isMoment(input) ? input.clone() : moment(input))
        : parseWireLocalTime(value);

    return `${parsed.format('HH:mm:ss')}${getCanonicalFraction(parsed)}`;

}

// ----------------------------------------------------------------------------------

function compareDatetimes(a, b, granularity) {

    granularity = granularity || 'second';

    if (!a && !b) return 0;
    if (!a) return -1;
    if (!b) return 1;

    var aa = parseDatetime(a),
        bb = parseDatetime(b);

    if (aa.isSame(bb, granularity)) return 0;
    if (aa.isAfter(bb, granularity)) return 1;
    return -1;

};

function compareTzDatetimes(timeZone, a, b, granularity) {

    granularity = granularity || 'second';

    if (!a && !b) return 0;
    if (!a) return -1;
    if (!b) return 1;

    var aa = parseTzDatetime(timeZone, a),
        bb = parseTzDatetime(timeZone, b);

    if (aa.isSame(bb, granularity)) return 0;
    if (aa.isAfter(bb, granularity)) return 1;
    return -1;

};

// ----------------------------------------------------------------------------------

function convertDateToLong(m, timeZone) {

    if (!moment.isMoment(m)) {
        if (timeZone)
            m = parseTzDatetime(timeZone, m);
        else
            m = parse(timeZone, m);
    }

    var l = m.year() * 10000 + (m.month() + 1) * 100 + m.date();
    return l;

};

/**
 * Pochází z TD, kde se tím počítaly směny. 
 * Problém byl v tom, že směny měly začátek v jednom dni, a konec v jiném.
 * 
 * @param {int} offset - n-tá minuta směny
 * @param {datetime} intervalDate - datum směny
 * @param {int} intervalBase - začátek směny jako n-tá minuta dne
 * @param {string} timeZone - čacová zóna
 * @returns 
 */
function convertIntervalOffsetToDatetime(offset, intervalDate, intervalBase, timeZone) {

    const config = getConfig();

    if (timeZone)
        intervalDate = parseTzDatetime(timeZone, intervalDate);
    else
        intervalDate = parseDatetime(intervalDate);

    if (offset === null || offset === undefined || intervalBase === null || intervalBase === undefined) {
        if (!intervalDate) {
            if (timeZone)
                return newTzDatetime(timeZone, config.locale);
            return newDatetime(config.locale);
        }
        return intervalDate;
    }

    return intervalDate.clone().add(intervalBase + offset, 'minutes');

};

/**
 *                  | 
 *                  | <--- interval offset = 120 min ---> [ <--- inteval range = 120 min ---> ] 
 * 2025-01-01 00:00 ^                    2025-01-01 02:00 ^                  2025-01-01 04:00 ^
 *
 * @param {*} date - datum, který převádím
 * @param {*} intervalRange - šířka intervalu v minutách (typicky - trvání směny) 
 * @param {*} intervalOffset - začátek intervalu jako n-tá minuta dne
 * @param {*} timeZone - časová zóna
 * @returns Offset zadaného datumu jako počet minut od začátku intervalu.
 */
function convertDatetimeToIntervalOffset(date, intervalRange, intervalOffset, timeZone) {

    if (date === null || date === undefined)
        return 0;
    if (intervalOffset === null || intervalOffset === undefined)
        return 0;

    if (timeZone)
        date = parseTzDatetime(timeZone, date);
    else
        date = parse(date);

    var m = date.hours() * 60 + date.minutes();
    if (m < intervalOffset)
        m = m + 60 * 24;
    m = m - intervalOffset;

    return (m < 0 ? 0 : (m > intervalRange ? intervalRange : m));

};

function addTimeZone(t, timeZone) {
    if (!moment.isMoment(t))
        throw new Error('Moment datetime value expected!');
    let zoned = tznow(timeZone);
    zoned.year(t.year()).month(t.month()).day(t.day()).hour(t.hour()).minute(t.minute()).second(t.second()).millisecond(t.millisecond());
    return zoned;
};

function removeTimeZone(t) {
    if (!moment.isMoment(t))
        throw new Error('Moment datetime value expected!');
    let dezoned = now();
    dezoned.year(t.year()).month(t.month()).day(t.day()).hour(t.hour()).minute(t.minute()).second(t.second()).millisecond(t.millisecond());
    return dezoned;
};

// ----------------------------------------------------------------------------------

export {
    formatBool,
    parseBool,
    //
    formatNumber,
    parseNumber,
    //
    formatBytes,
    parseBytes,
    //
    formatString,
    parseString,
    //
    formatTimespan,
    parseTimespan,
    //
    newDatetime,
    newTzDatetime,
    parseDatetime,
    parseTzDatetime,
    formatDatetime,
    formatTzDatetime,
    formatIsoDatetime,
    parseWireUtcDatetime,
    serializeWireUtcDatetime,
    parseWireZonedDatetime,
    serializeWireZonedDatetime,
    parseWireLocalDatetime,
    serializeWireLocalDatetime,
    parseWireLocalDate,
    serializeWireLocalDate,
    parseWireLocalTime,
    serializeWireLocalTime,
    compareDatetimes,
    compareTzDatetimes,
    convertDateToLong,
    convertIntervalOffsetToDatetime,
    convertDatetimeToIntervalOffset,
    addTimeZone,
    removeTimeZone,
};

